import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Form, Table, message, Input, DatePicker, Button } from "antd";
import { SearchOutlined, ClearOutlined } from "@ant-design/icons";
import moment from "moment";

import DataTotalDisplay from "../../component/data-total-display/data-total-display.component";

import { setMenu } from "../../redux/reducers/navigationSlice";
import { setUserAccount } from "../../redux/reducers/userAccountSlice";

import InvitationAPI from "../../api/InvitationAPI";

import "./invitations-report.styles.css";

const initialFormValues = {
  residence: "",
  visitor: "",
  cedula: "",
  guestCount: "",
  accessCode: "",
  createdDate: "",
  dueDate: "",
};

const tableColumns = [
  { title: "Residencia", dataIndex: "Residence" },
  { title: "Nombre Invitado", dataIndex: "Visitor" },
  { title: "ID Invitado", dataIndex: "Cedula" },
  { title: "Cant. Acompañantes", dataIndex: "GuestCount" },
  { title: "Código Acceso", dataIndex: "AccessCode" },
  { title: "Grupo de Horario", dataIndex: "ScheduleGroupName" },
  { title: "Fecha Creación", dataIndex: "CreatedDate" },
  { title: "Fecha Vigencia", dataIndex: "DueDate" },
];

const InvitationsReportPage = () => {
  const dispatch = useDispatch();
  const [messageApi, messageContext] = message.useMessage();
  const [searchForm] = Form.useForm();
  const token = useSelector((state) => state.userAccount.user.token);

  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageSize, setCurrentPageSize] = useState(10);
  const [invitationsListData, setInvitationsListData] = useState({
    invitations: [],
    totalCount: 0,
  });

  const loadInitialData = useCallback(async () => {
    messageApi.open({ type: "loading", content: "Cargando..." });

    try {
      const response = await InvitationAPI.getInvitationsList(token);

      if (!response.data.success) {
        messageApi.open({
          type: "error",
          content: response.data.message,
        });
        return;
      }

      setInvitationsListData(response.data.data);
    } catch (ex) {
      console.error(ex.message);

      let message = "Servicio no disponible, intente más tarde. ";

      if (ex.code !== "ERR_NETWORK") {
        message = ex.response.data.message;

        if (ex.response.status === 401) dispatch(setUserAccount(null));
      }

      messageApi.open({
        type: "error",
        content: message,
      });
    } finally {
      messageApi.destroy();
    }
  }, [dispatch, messageApi, token]);

  const loadInvitationsList = async (activePage = 1, activePageSize = 10) => {
    messageApi.open({ type: "loading", content: "Cargando..." });

    try {
      const createdDate = searchForm.getFieldValue("createdDate")
        ? moment(
            new Date(
              searchForm.getFieldValue("createdDate")
            ).toLocaleDateString(),
            "DD/MM/YYYY"
          ).format("YYYY-MM-DD")
        : "";
      const dueDate = searchForm.getFieldValue("dueDate")
        ? moment(
            new Date(searchForm.getFieldValue("dueDate")).toLocaleDateString(),
            "DD/MM/YYYY"
          ).format("YYYY-MM-DD")
        : "";

      const response = await InvitationAPI.getInvitationsList(token, {
        page: activePage,
        pageSize: activePageSize,
        ...searchForm.getFieldsValue(),
        createdDate,
        dueDate,
      });

      if (!response.data.success) {
        messageApi.open({
          type: "error",
          content: response.data.message,
        });
        return;
      }

      setCurrentPage(activePage);
      setInvitationsListData(response.data.data);
    } catch (ex) {
      console.error(ex.message);

      let message = "Servicio no disponible, intente más tarde. ";

      if (ex.code !== "ERR_NETWORK") {
        message = ex.response.data.message;

        if (ex.response.status === 401) {
          dispatch(setUserAccount(null));
        }
      }

      messageApi.open({ type: "error", content: message });
    } finally {
      messageApi.destroy();
    }
  };

  useEffect(() => {
    dispatch(setMenu("invitationsReport"));
    loadInitialData();
  }, [dispatch, loadInitialData]);

  return (
    <div className="invitations-report">
      {messageContext}
      <Form
        className="invitations-search-form"
        initialValues={initialFormValues}
        form={searchForm}
        onFinish={() => loadInvitationsList()}
        size="small"
        labelCol={{
          span: 10,
        }}
        wrapperCol={{
          span: 10,
        }}
      >
        <Form.Item label="Residencia" name="residence">
          <Input />
        </Form.Item>
        <Form.Item label="Invitado" name="visitor">
          <Input />
        </Form.Item>
        <Form.Item label="ID invitado" name="cedula">
          <Input />
        </Form.Item>
        <Form.Item label="Cant. acompañantes" name="guestCount">
          <Input />
        </Form.Item>
        <Form.Item label="Código acceso" name="accessCode">
          <Input />
        </Form.Item>
        <Form.Item label="Fecha creado" name="createdDate">
          <DatePicker format="DD/MM/YYYY" />
        </Form.Item>
        <Form.Item label="Fecha vencimiento" name="dueDate">
          <DatePicker format="DD/MM/YYYY" />
        </Form.Item>
        <div className="buttons-container">
          <Button type="primary" htmlType="submit">
            <SearchOutlined />
            Buscar
          </Button>
          <Button
            onClick={() => {
              searchForm.resetFields();
              searchForm.submit();
              setCurrentPage(1);
            }}
          >
            <ClearOutlined />
            Limpiar
          </Button>
        </div>
      </Form>
      <DataTotalDisplay
        currentCount={invitationsListData.invitations.length}
        totalCount={invitationsListData.totalCount}
      />
      <Table
        columns={tableColumns}
        dataSource={invitationsListData.invitations}
        pagination={{
          current: currentPage,
          pageSize: currentPageSize,
          total: invitationsListData.totalCount,
        }}
        onChange={async (e) => {
          await loadInvitationsList(e.current, e.pageSize);
          setCurrentPage(e.current);
          setCurrentPageSize(e.pageSize);
        }}
        size="small"
      />
    </div>
  );
};

export default InvitationsReportPage;
